import { useMutation, useQuery } from '@apollo/client/react';
import { useReducer, useState } from 'react';
import { CalendarX, Clock, Pencil, Trash } from 'react-bootstrap-icons';

import type { SpecificDayAvailability, TimeIntervalInput } from '@repo/frontend-shared';
import {
  CreateSpecificDayAvailabilityDocument,
  DeleteSpecificDayAvailabilityDocument,
  GetSpecificDayAvailabilitiesDocument,
  UpdateSpecificDayAvailabilityDocument,
} from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSCard } from '@repo/sds/SDSCard';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSSwitch } from '@repo/sds/SDSSwitch';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';
import { toast } from '@repo/sds/utils/toast';
import { CalendarDateSelector } from '@repo/ui/fixtures/smallCalendar/components/CalendarDateSelector/CalendarDateSelector';
import { TimezoneSelect } from '@repo/ui/fixtures/timezoneSelect/components/TimezoneSelect/TimezoneSelect';

import { TimeRangeEditor } from './TimeRangeEditor';
import { createTimeRange, type TimeRange } from './time-range';

function formatOverrideDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
}

interface DateOverridesSectionProps {
  userId: string;
}

interface DateOverridesContentProps {
  loading: boolean;
  overrides: SpecificDayAvailability[];
  onEdit: (override: SpecificDayAvailability) => void;
  onDelete: (id: string) => void;
}

function DateOverrideCard({
  override,
  onEdit,
  onDelete,
}: {
  override: SpecificDayAvailability;
  onEdit: (override: SpecificDayAvailability) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <SDSCard>
      <SDSFlexbox direction="row" justifyContent="space-between" alignItems="center">
        <SDSFlexbox direction="column" gap="text-small">
          <SDSFlexbox direction="row" gap="text-small" alignItems="center">
            <SDSText type="body-large-bold" text={formatOverrideDate(override.date)} />
            {override.reason && <SDSText type="body-small" text={`· ${override.reason}`} color="inactive" />}
          </SDSFlexbox>
          <SDSFlexbox direction="row" gap="text-small" alignItems="center">
            <SDSIcon icon={<Clock />} color="inactive" />
            <SDSText
              type="body-small"
              text={override.isAvailable ? `${override.intervals.length} slot(s)` : 'Unavailable'}
              color="inactive"
            />
          </SDSFlexbox>
        </SDSFlexbox>
        <SDSFlexbox direction="row" gap="text-small">
          <SDSButton
            mode="outline"
            size="small"
            leftIcon={<Pencil />}
            text=""
            onClick={() => onEdit(override)}
            ariaLabel="Edit date override"
          />
          <SDSButton
            mode="outline"
            size="small"
            leftIcon={<Trash />}
            text=""
            status="alert"
            onClick={() => onDelete(override.id)}
            ariaLabel="Delete date override"
          />
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSCard>
  );
}

function DateOverridesContent({ loading, overrides, onEdit, onDelete }: DateOverridesContentProps) {
  if (loading) {
    return (
      <SDSCard>
        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText type="body-medium" text="Loading..." color="inactive" />
        </SDSFlexbox>
      </SDSCard>
    );
  }

  if (overrides.length === 0) {
    return (
      <SDSCard>
        <SDSFlexbox direction="column" gap="text-medium" alignItems="center">
          <SDSIcon icon={<CalendarX />} color="inactive" />
          <SDSText type="body-medium" text="No date overrides" />
          <SDSText
            type="body-small"
            text="Add overrides for specific dates like holidays or special hours"
            color="inactive"
          />
        </SDSFlexbox>
      </SDSCard>
    );
  }

  return (
    <SDSFlexbox direction="column" gap="card-medium">
      {overrides.map(override => (
        <DateOverrideCard key={override.id} override={override} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </SDSFlexbox>
  );
}

export function DateOverridesSection({ userId }: DateOverridesSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<SpecificDayAvailability | null>(null);

  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + 6);
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 3);

  const { data, loading, refetch } = useQuery<{ specificDayAvailabilities: SpecificDayAvailability[] }>(
    GetSpecificDayAvailabilitiesDocument,
    {
      skip: !userId,
      variables: {
        userId,
        startDate: startOfDay(startDate),
        endDate: startOfDay(endDate),
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [deleteOverride] = useMutation(DeleteSpecificDayAvailabilityDocument, {
    onCompleted: () => {
      toast.success('Date override removed');
      void refetch();
    },
    onError: () => toast.error('Failed to remove date override'),
  });

  const handleAdd = () => {
    setEditingOverride(null);
    setIsModalOpen(true);
  };

  const handleEdit = (o: SpecificDayAvailability) => {
    setEditingOverride(o);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOverride({ variables: { id } });
    } catch {
      // toast already handled by onError callback
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOverride(null);
  };

  const overrides = data?.specificDayAvailabilities ?? [];

  return (
    <>
      <SDSFlexbox direction="column" gap="content-large">
        <SDSFlexbox direction="row" justifyContent="space-between" alignItems="center">
          <SDSText type="h5" text="Date Overrides" />
          <SDSButton mode="outline" size="small" text="+ Add Date Override" onClick={handleAdd} />
        </SDSFlexbox>

        <DateOverridesContent loading={loading} overrides={overrides} onEdit={handleEdit} onDelete={handleDelete} />
      </SDSFlexbox>

      <DateOverrideEditModal
        key={editingOverride?.id ?? 'new'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        existing={editingOverride}
        onSaved={() => {
          void refetch();
        }}
      />
    </>
  );
}

interface DateOverrideEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  existing: SpecificDayAvailability | null;
  onSaved: () => void;
}

interface DateOverrideEditState {
  selectedDate: Date;
  isAvailable: boolean;
  timeRanges: TimeRange[];
  reason: string;
  timezone: string;
  saving: boolean;
}

function createOverrideTimeRanges(existing: SpecificDayAvailability): TimeRange[] {
  const intervals = existing.intervals;
  return intervals.length > 0
    ? intervals.map(interval => createTimeRange(interval.start, interval.end))
    : [createTimeRange()];
}

function createDateOverrideEditState(existing: SpecificDayAvailability | null): DateOverrideEditState {
  if (!existing) {
    return {
      selectedDate: new Date(),
      isAvailable: true,
      timeRanges: [createTimeRange()],
      reason: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      saving: false,
    };
  }

  return {
    selectedDate: new Date(existing.date),
    isAvailable: existing.isAvailable,
    timeRanges: createOverrideTimeRanges(existing),
    reason: existing.reason ?? '',
    timezone: existing.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    saving: false,
  };
}

function dateOverrideEditReducer(
  state: DateOverrideEditState,
  changes: Partial<DateOverrideEditState>,
): DateOverrideEditState {
  return { ...state, ...changes };
}

function getDateOverrideModalTitle(existing: SpecificDayAvailability | null): string {
  return existing ? 'Edit Date Override' : 'Add Date Override';
}

function DateOverrideEditModal({ isOpen, onClose, existing, onSaved }: DateOverrideEditModalProps) {
  const [editState, updateEditState] = useReducer(dateOverrideEditReducer, existing, createDateOverrideEditState);
  const { selectedDate, isAvailable, timeRanges, reason, timezone, saving } = editState;

  const [createOverride] = useMutation(CreateSpecificDayAvailabilityDocument, {
    onCompleted: () => {
      toast.success('Date override created');
      onSaved();
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const [updateOverride] = useMutation(UpdateSpecificDayAvailabilityDocument, {
    onCompleted: () => {
      toast.success('Date override updated');
      onSaved();
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const handleSave = async () => {
    updateEditState({ saving: true });
    const intervals: TimeIntervalInput[] = isAvailable
      ? timeRanges.flatMap(range => (range.start && range.end ? [{ start: range.start, end: range.end }] : []))
      : [];
    const dateStr = toDateString(selectedDate);
    const cleanReason = reason.trim() || undefined;

    const saveRequest = existing
      ? updateOverride({
          variables: {
            input: { id: existing.id, date: dateStr, timezone, isAvailable, intervals, reason: cleanReason },
          },
        })
      : createOverride({
          variables: {
            input: { date: dateStr, timezone, isAvailable, intervals, reason: cleanReason },
          },
        });
    await saveRequest.finally(() => updateEditState({ saving: false }));
  };

  const handleTimezoneSelect = (tz: { iana: string }) => updateEditState({ timezone: tz.iana });

  return (
    <SDSDialog isOpen={isOpen} onClose={onClose} size="medium">
      <SDSFlexbox direction="column" gap="content-default">
        <SDSText type="h5" text={getDateOverrideModalTitle(existing)} />

        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText type="body-medium" text="Select Date" />
          <CalendarDateSelector
            initialDate={selectedDate}
            onDateSelect={(selectedDate: Date) => updateEditState({ selectedDate })}
          />
        </SDSFlexbox>

        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText type="body-medium" text="Timezone" />
          <TimezoneSelect defaultTimezone={timezone} onSelectTimezone={handleTimezoneSelect} />
        </SDSFlexbox>

        <SDSFlexbox direction="row" gap="text-medium" alignItems="center">
          <SDSSwitch checked={isAvailable} onChange={() => updateEditState({ isAvailable: !isAvailable })} />
          <SDSText type="body-medium" text={isAvailable ? 'Available' : 'Unavailable all day'} />
        </SDSFlexbox>

        {isAvailable && (
          <SDSFlexbox direction="column" gap="text-small">
            <SDSText type="body-medium" text="Time Slots" />
            <TimeRangeEditor ranges={timeRanges} onChange={timeRanges => updateEditState({ timeRanges })} />
          </SDSFlexbox>
        )}

        <SDSFlexbox direction="column" gap="text-small">
          <SDSText type="body-medium" text="Reason (optional)" />
          <SDSTextInput
            value={reason}
            onChange={reason => updateEditState({ reason })}
            placeholder="e.g. Holiday, Sick day, Conference"
            ariaLabel="Date override reason"
          />
        </SDSFlexbox>

        <SDSFlexbox direction="row" gap="text-medium" justifyContent="flex-end">
          <SDSButton mode="outline" text="Cancel" onClick={onClose} />
          <SDSButton mode="primary" text={saving ? 'Saving...' : 'Save'} onClick={handleSave} isDisabled={saving} />
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}
